import { apiFetch } from "../../lib/api";
import { getSupabase } from "../../lib/supabase";

import { type CitySlug, OTHER_CITY_SLUG } from "./cities";

export type CityRequestCounts = Partial<Record<CitySlug, number>>;

type CountsRow = { city_slug: CitySlug; request_count: number };

type Result<T> = { data: T | null; error: unknown };

// Local Supabase shape; mirrors stations/api.ts pattern until
// `supabase gen types` populates `Database`.
type SupabaseLike = {
  from: (table: string) => {
    select: (cols: string) => Promise<Result<CountsRow[]>>;
  };
};

function toError(error: unknown, context: string): Error {
  if (error instanceof Error) return error;
  const message =
    typeof error === "object" && error !== null && "message" in error
      ? String((error as { message: unknown }).message)
      : "unknown error";
  return new Error(`${context}: ${message}`, { cause: error });
}

export async function fetchCityRequestCounts(): Promise<CityRequestCounts> {
  const supabase = getSupabase() as unknown as SupabaseLike;
  const { data, error } = await supabase
    .from("city_request_counts")
    .select("city_slug, request_count");
  if (error) throw toError(error, "city_request_counts.select");
  const counts: CityRequestCounts = {};
  for (const row of data ?? []) counts[row.city_slug] = row.request_count;
  return counts;
}

export type SubmitCityRequestArgs = {
  citySlug: CitySlug;
  customName?: string;
  locale?: string;
  cfTurnstileResponse?: string;
};

export async function submitCityRequest(args: SubmitCityRequestArgs): Promise<void> {
  await apiFetch<void>("/city-requests", {
    method: "POST",
    body: {
      city_slug: args.citySlug,
      custom_name: args.citySlug === OTHER_CITY_SLUG ? (args.customName ?? null) : null,
      locale: args.locale ?? null,
      cf_turnstile_response: args.cfTurnstileResponse ?? null,
    },
  });
}
